import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, MoreHorizontal, Pill } from "lucide-react";
import { differenceInDays, format, formatDistanceToNowStrict } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useTranslation as useI18n } from "react-i18next";
import type { Baia } from "@/hooks/useHospitalizationSectors";
import { nextDoseAt } from "@/hooks/useHospitalizationSectors";

const AVATAR_COLORS = [
  "from-orange-200 to-amber-300 border-orange-200/60 text-orange-900",
  "from-purple-200 to-purple-300 border-purple-200/60 text-purple-900",
  "from-blue-200 to-blue-300 border-blue-200/60 text-blue-900",
  "from-emerald-200 to-emerald-300 border-emerald-200/60 text-emerald-900",
  "from-rose-200 to-rose-300 border-rose-200/60 text-rose-900",
];

function petAvatarColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function petAge(birthDate: string | null): string | null {
  if (!birthDate) return null;
  const days = differenceInDays(new Date(), new Date(birthDate));
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.floor(days / 30)}m`;
  const years = Math.floor(days / 365);
  return `${years} ${years === 1 ? "ano" : "anos"}`;
}

interface BaiaCardProps {
  baia: Baia;
  onAdmit?: () => void;
}

export function BaiaCard({ baia, onAdmit }: BaiaCardProps) {
  const { t } = useTranslation("hospitalization");
  const { i18n } = useI18n();
  const locale = i18n.language === "pt" ? ptBR : undefined;
  const hosp = baia.hospitalization_ativa;

  /* ── Empty bay ── */
  if (!hosp) {
    return (
      <div className="rounded-2xl border border-dashed border-border/70 bg-[repeating-linear-gradient(135deg,transparent_0_10px,hsl(var(--muted)/0.35)_10px_20px)] overflow-hidden flex flex-col min-h-[180px]">
        <div className="px-3 py-2.5 flex items-center gap-2">
          <span className="font-mono text-[11px] font-bold text-muted-foreground bg-background/70 px-1.5 py-0.5 rounded">
            {baia.nome}
          </span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-2.5 py-4 px-3 text-center">
          <span className="text-xs font-semibold text-muted-foreground">{t("dashboard.livre")}</span>
          {onAdmit && (
            <button
              onClick={onAdmit}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-foreground text-background hover:opacity-80 transition-opacity"
            >
              <Plus className="h-3 w-3" />
              {t("dashboard.admitir")}
            </button>
          )}
        </div>
      </div>
    );
  }

  /* ── Occupied bay ── */
  const avatarColor = petAvatarColor(hosp.pet.name);
  const initial = hosp.pet.name.charAt(0).toUpperCase();
  const age = petAge(hosp.pet.birth_date);
  const dias = differenceInDays(new Date(), new Date(hosp.entrada_at));
  const isCritical = hosp.recentRecords.some((r) => r.condicao_geral !== null && r.condicao_geral <= 2);

  /* next due prescription */
  const sortedRx = [...hosp.activePrescriptions]
    .map((p) => ({ p, next: nextDoseAt(p) }))
    .filter((x) => x.next !== null)
    .sort((a, b) => a.next!.getTime() - b.next!.getTime());
  const nextRx = sortedRx[0] ?? null;
  const nextRxOverdue = nextRx ? nextRx.next! < new Date() : false;
  const nextRxCountdown = nextRx
    ? formatDistanceToNowStrict(nextRx.next!, { locale, addSuffix: false })
    : null;

  return (
    <div className={`rounded-2xl border bg-card shadow-sm overflow-hidden flex flex-col group ${isCritical ? "border-red-300 dark:border-red-800" : "border-border"}`}>
      {isCritical && <div className="h-0.5 bg-gradient-to-r from-orange-400 to-red-500" />}

      {/* Bay header */}
      <div className="px-3 py-2.5 flex items-center gap-2 border-b border-border">
        <span className="font-mono text-[11px] font-bold text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
          {baia.nome}
        </span>
        {hosp.motivo && (
          <span className="text-xs font-medium text-foreground truncate flex-1">{hosp.motivo}</span>
        )}
        <span className={`ml-auto shrink-0 font-mono text-[10px] font-semibold px-2 py-0.5 rounded-full ${dias >= 7 ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
          dia {dias === 0 ? 1 : dias}
        </span>
        <button className="text-muted-foreground hover:text-foreground">
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Pet row */}
      <Link to={`/hospitalization/${hosp.id}`} className="px-3 pt-3 pb-2 flex items-start gap-3 hover:bg-muted/30 transition-colors">
        <div className={`w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center bg-gradient-to-br border text-base font-bold ${avatarColor}`}>
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-sm leading-tight text-foreground">{hosp.pet.name}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {age ?? t("dashboard.internado")}
          </p>
          {hosp.recentRecords[0]?.peso_kg && (
            <p className="font-mono text-[11px] font-semibold text-foreground mt-1">
              ⊞ {hosp.recentRecords[0].peso_kg} kg
            </p>
          )}
        </div>
      </Link>

      {/* Next action */}
      {nextRx && (
        <div className={`mx-3 mb-3 rounded-xl p-2.5 flex items-center gap-2.5 border ${
          nextRxOverdue
            ? "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800"
            : "bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-900/40"
        }`}>
          <div className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center ${
            nextRxOverdue ? "bg-red-100 dark:bg-red-900/40 text-red-600" : "bg-purple-100 dark:bg-purple-900/40 text-purple-600"
          }`}>
            <Pill className="h-3.5 w-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-foreground truncate">
              {nextRx.p.medicamento} · {nextRx.p.dose}
            </p>
            <p className={`text-[11px] font-semibold mt-0.5 flex items-center gap-1.5 ${nextRxOverdue ? "text-red-600" : "text-muted-foreground"}`}>
              {nextRx.next && format(nextRx.next, "HH:mm")}
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-mono ${
                nextRxOverdue
                  ? "bg-red-100 dark:bg-red-900/40 text-red-700"
                  : "bg-purple-100 dark:bg-purple-900/40 text-purple-700"
              }`}>
                {nextRxOverdue ? "atrasado" : `em ${nextRxCountdown}`}
              </span>
            </p>
          </div>
          <button className={`shrink-0 text-[11px] font-bold px-2.5 py-1.5 rounded-full text-white ${nextRxOverdue ? "bg-red-500" : "bg-purple-500"}`}>
            aplicar →
          </button>
        </div>
      )}

      {/* Last records */}
      {hosp.recentRecords.length > 0 && (
        <div className="px-3 pb-3 border-t border-dashed border-border pt-2.5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              {t("dashboard.ultimosRegistros")}
            </span>
            <Link to={`/hospitalization/${hosp.id}`} className="text-[10px] font-semibold text-muted-foreground hover:text-primary">
              {t("dashboard.verTudo")}
            </Link>
          </div>
          <div className="flex flex-col gap-1">
            {hosp.recentRecords.map((r) => (
              <div key={r.id} className="grid grid-cols-[44px_1fr] gap-2 text-xs">
                <span className="font-mono text-[10px] text-muted-foreground pt-0.5">
                  {format(new Date(r.registrado_at), "HH:mm")}
                </span>
                <span className="text-foreground/80 leading-snug line-clamp-1">
                  {r.observacoes ?? (r.peso_kg ? `Peso: ${r.peso_kg} kg` : "—")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
